/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import React, { useMemo, useEffect } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { Avatar, Dropdown, Skeleton } from 'antd';
import { UserOutlined } from '@ant-design/icons';

import { useAppDispatch, useAppSelector } from '@/hooks/useRedux';
import { signOut } from '@/redux/auth/signin/slice';
import ThemedButton from '@/components/common/buttons/Button';

import { FiFileText } from 'react-icons/fi';
import { HiClipboardDocumentCheck } from 'react-icons/hi2';
import { IoReceiptSharp } from 'react-icons/io5';
import { TbRubberStamp } from 'react-icons/tb';

import { selectOutstandingBalanceState } from '@/redux/customer/selectors';
import { fetchOutstandingBalance } from '@/redux/customer/thunks';
import { useUserMenuCounts } from '@/hooks/useUserMenuCounts'; // <-- NEW IMPORT

const currency = (n: number | string = 0) => {
  try {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(n || 0));
  } catch {
    return `KES ${Number(n || 0).toFixed(2)}`;
  }
};

const MyAccountDropDown: React.FC = () => {
  const router = useRouter();
  const pathname = usePathname();
  const dispatch = useAppDispatch();

  const reduxUser = useAppSelector((s: any) => s.auth?.signin?.user);
  const user = reduxUser;
  const { data, status } = useAppSelector(selectOutstandingBalanceState);

  useEffect(() => {
    dispatch(fetchOutstandingBalance());
  }, [dispatch]);

  const outstanding = Number(data?.outstanding_balance ?? 0);

  // 🔹 GET COUNTS FROM HOOK (ONLY CHANGE FOR COUNTS)
  const {
    invoicesCount,
    receiptsCount,
    applicationsCount,
    authorizationsCount,
  } = useUserMenuCounts();

  // persistence
  useEffect(() => {
    if (!user || typeof window === 'undefined') return;
    const displayName = user.first_name?.trim() || user.username?.trim();
    if (displayName) localStorage.setItem('username', displayName);
    try {
      localStorage.setItem('userProfile', JSON.stringify(user));
    } catch {}
  }, [user]);

  const username =
    user?.first_name?.trim() ||
    user?.username?.trim() ||
    (typeof window !== 'undefined'
      ? localStorage.getItem('username') || 'User'
      : 'User');

  const initials = useMemo(() => {
    const n = username.trim();
    const parts = n.split(' ');
    return (
      <i className="capitalize">
        {(parts[0]?.[0] || 'U') + (parts[1]?.[0] || '')}
      </i>
    );
  }, [username]);

  const handleLogout = () => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('username');
      localStorage.removeItem('userProfile');
    }
    dispatch(signOut());
    router.push('/account/signin');
  };

  const isActive = (key: string) => pathname?.includes(key);

  const iconWrap =
    'w-9 h-9 rounded-full flex items-center justify-center bg-gray-100 transition';

  const iconActive =
    'bg-[var(--color-primary)]/10 border border-[var(--color-primary)]';

  const hoverWrap =
    'hover:bg-gray-50 transition rounded-lg px-2 py-2 cursor-pointer';

  return (
    <Dropdown
      trigger={['click']}
      placement="bottomRight"
      menu={{ items: [] }}
      overlayStyle={{ zIndex: 40 }}
      dropdownRender={() => (
        <div className="w-80 rounded-2xl bg-white shadow-xl overflow-hidden border border-gray-100 !z-40">
          {/* HEADER */}
          <div className="px-5 pt-4 pb-3 border-b border-gray-100 flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-gray-900">
                My Account
              </div>
              {/* <div className="text-xs text-gray-500">
                Signed in as {username}
              </div> */}
            </div>

            <Avatar size={32} icon={<UserOutlined />}>
              {initials}
            </Avatar>
          </div>

          {/* ⭐⭐⭐ OUTSTANDING BALANCE – RESTORED ⭐⭐⭐ */}
          <div className="px-5 py-3 bg-green-50">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-xs font-medium text-gray-700">
                  Outstanding balance
                </div>
                <Link
                  href="/account/?invoices"
                  className="text-xs text-[var(--color-primary)] hover:underline"
                >
                  View all pending invoices
                </Link>
              </div>

              <div className="text-right">
                {status === 'loading' ? (
                  <Skeleton.Input
                    active
                    size="default"
                    style={{ width: 120 }}
                  />
                ) : (
                  <div className="text-sm font-bold rounded text-gray-900">
                    {currency(outstanding)}
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* END OUTSTANDING BALANCE */}

          {/* MENU ITEMS */}
          <div className="px-5 py-3 space-y-3 text-sm">
            {/* INVOICES */}
            <Link href="/account/?invoices">
              <div
                className={`${hoverWrap} flex items-center justify-between ${
                  isActive('invoices') ? 'bg-gray-100' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`${iconWrap} ${
                      isActive('invoices') ? iconActive : ''
                    }`}
                  >
                    <FiFileText className="text-[var(--color-primary)] text-lg" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">Invoices</div>
                    <div className="text-xs text-gray-500">
                      All generated invoices
                    </div>
                  </div>
                </div>
                <span className="font-semibold text-[var(--color-primary)]">
                  {invoicesCount}
                </span>
              </div>
            </Link>

            {/* APPLICATIONS */}
            <Link href="/account/?applications">
              <div
                className={`${hoverWrap} flex items-center justify-between ${
                  isActive('applications') ? 'bg-gray-100' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`${iconWrap} ${
                      isActive('applications') ? iconActive : ''
                    }`}
                  >
                    <HiClipboardDocumentCheck className="text-[var(--color-primary)] text-lg" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">
                      Applications
                    </div>
                    <div className="text-xs text-gray-500">
                      Submitted applications
                    </div>
                  </div>
                </div>
                <span className="font-semibold text-[var(--color-primary)]">
                  {applicationsCount}
                </span>
              </div>
            </Link>

            {/* RECEIPTS */}
            <Link href="/account/?receipts">
              <div
                className={`${hoverWrap} flex items-center justify-between ${
                  isActive('receipts') ? 'bg-gray-100' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`${iconWrap} ${
                      isActive('receipts') ? iconActive : ''
                    }`}
                  >
                    <IoReceiptSharp className="text-[var(--color-primary)] text-lg" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">Receipts</div>
                    <div className="text-xs text-gray-500">
                      All payments received
                    </div>
                  </div>
                </div>
                <span className="font-semibold text-[var(--color-primary)]">
                  {receiptsCount}
                </span>
              </div>
            </Link>

            {/* AUTHORIZATIONS */}
            <Link href="/account/?authorizations">
              <div
                className={`${hoverWrap} flex items-center justify-between ${
                  isActive('authorizations') ? 'bg-gray-100' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`${iconWrap} ${
                      isActive('authorizations') ? iconActive : ''
                    }`}
                  >
                    <TbRubberStamp className="text-[var(--color-primary)] text-lg" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">
                      Authorizations
                    </div>
                    <div className="text-xs text-gray-500">
                      Licenses & permits
                    </div>
                  </div>
                </div>
                <span className="font-semibold text-[var(--color-primary)] ">
                  {authorizationsCount}
                </span>
              </div>
            </Link>
          </div>

          {/* FOOTER */}
          <div className="px-5 py-4 border-t border-gray-100 flex justify-between">
            <Link
              href="/account/profile"
              className="text-xs text-gray-600 hover:text-gray-800"
            >
              View Profile
            </Link>

            <button
              onClick={handleLogout}
              className="text-xs hover:cursor-pointer font-medium text-red-600 hover:text-red-700"
            >
              Logout
            </button>
          </div>
        </div>
      )}
    >
      <div
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <ThemedButton theme="primary" text="My Account" />
      </div>
    </Dropdown>
  );
};

export default MyAccountDropDown;
